/**
 * Shared focus/blur state for fields that render an `ExpressionPreview`
 * overlay. Centralises the "don't blur while the user is selecting text
 * in the preview" guard that every field would otherwise duplicate.
 *
 * Usage:
 *   const { isFocused, containerHandlers, previewHandlers } = useFieldPreviewFocus();
 *
 *   <div {...containerHandlers}>
 *     <TokenizedInput ... />
 *     {isFocused && value && (
 *       <ExpressionPreview mode="overlay" {...previewHandlers} ... />
 *     )}
 *   </div>
 */
import { useCallback, useMemo, useRef, useState, type FocusEvent } from 'react';

export interface UseFieldPreviewFocusResult {
  isFocused: boolean;
  containerHandlers: {
    onFocus: () => void;
    onBlur: (event: FocusEvent<HTMLElement>) => void;
  };
  previewHandlers: {
    onMouseDown: () => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
  };
}

export const useFieldPreviewFocus = (): UseFieldPreviewFocusResult => {
  const [isFocused, setIsFocused] = useState(false);
  // Tracks an in-progress mouse interaction inside the preview so we can
  // ignore the spurious blur the editor receives during text selection.
  const isInteractingRef = useRef(false);

  const onFocus = useCallback(() => setIsFocused(true), []);

  const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    const movingOutside = !event.currentTarget.contains(event.relatedTarget as Node | null);
    if (isInteractingRef.current && !movingOutside) return;
    if (movingOutside) setIsFocused(false);
  }, []);

  const previewHandlers = useMemo(
    () => ({
      onMouseDown: () => { isInteractingRef.current = true; },
      onMouseUp: () => { isInteractingRef.current = false; },
      onMouseLeave: () => { isInteractingRef.current = false; },
    }),
    [],
  );

  return {
    isFocused,
    containerHandlers: { onFocus, onBlur },
    previewHandlers,
  };
};
